import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createVNode as _createVNode, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["data-testid"]
const _hoisted_2 = ["data-testid"]
const _hoisted_3 = ["data-testid"]
const _hoisted_4 = {
  class: "w-10 h-10 text-negative",
  viewBox: "0 0 24 24",
  "aria-hidden": "true"
}
const _hoisted_5 = ["d"]
const _hoisted_6 = { class: "text-sm text-textPrimary" }
const _hoisted_7 = ["data-testid"]
const _hoisted_8 = {
  class: "w-10 h-10 text-neutral50",
  viewBox: "0 0 24 24",
  "aria-hidden": "true"
}
const _hoisted_9 = ["d"]
const _hoisted_10 = /*#__PURE__*/_createElementVNode("p", { class: "font-bold text-textPrimary" }, "Evidência indisponível", -1)
const _hoisted_11 = { class: "text-sm text-neutral50 max-w-sm" }
const _hoisted_12 = {
  key: 0,
  class: "flex flex-wrap gap-2",
  role: "tablist"
}
const _hoisted_13 = ["aria-selected", "data-testid", "onClick"]
const _hoisted_14 = {
  class: "w-4 h-4 shrink-0",
  viewBox: "0 0 24 24",
  "aria-hidden": "true"
}
const _hoisted_15 = ["d"]
const _hoisted_16 = { class: "truncate max-w-[160px]" }
const _hoisted_17 = ["data-testid"]
const _hoisted_18 = {
  class: "w-10 h-10 text-neutral50",
  viewBox: "0 0 24 24",
  "aria-hidden": "true"
}
const _hoisted_19 = ["d"]
const _hoisted_20 = { class: "font-bold text-textPrimary" }
const _hoisted_21 = {
  key: 0,
  class: "text-sm text-neutral50"
}
const _hoisted_22 = ["title"]

import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { mdiAlertCircleOutline, mdiFileOutline, mdiImageOffOutline } from '@mdi/js'

import Network from '@/Network'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import StepPreview from '@/components/designSystem/modals/AttendanceUploadModal/StepPreview.vue'
import type { CheckValidationDetailResponse } from '@/Network/Types/Responses/CheckValidationResponses'
import { CHECK_EVIDENCE_EVENT_TYPE_LABELS } from '@/constants/check-validation.constants'
import { getApiErrorMessage } from '@/utils/apiError'

import { isEvidenceUnavailableError } from '../errors'

export interface CheckEvidenceContext {
  checkin: string | null
  checkout: string | null
  timezone: string
  doctorName: string | null
  crm: { number: string; uf: string } | null
}

type EvidenceFileStatus = 'loading' | 'ready' | 'unavailable' | 'error'

interface EvidenceFile {
  id: number
  name: string
  eventType: string | null
  url: string | null
  type: string
  status: EvidenceFileStatus
  error: string | null
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CheckEvidencePanel',
  props: {
    checkId: {},
    testidPrefix: { default: 'check-validation' }
  },
  emits: ["loaded", "unavailable", "available"],
  setup(__props: any, { expose: __expose, emit }) {

const props = __props;





const isLoading = ref(false)
const loadError = ref<string | null>(null)
const files = ref<EvidenceFile[]>([])
const selectedIndex = ref(0)
const rotation = ref(0)
const zoom = ref(1)

const currentFile = computed(() => files.value[selectedIndex.value] ?? null)

const allUnavailable = computed(
  () => files.value.length === 0 || files.value.every((file) => file.status === 'unavailable')
)

const fileLabel = (file: EvidenceFile): string =>
  (file.eventType && CHECK_EVIDENCE_EVENT_TYPE_LABELS[file.eventType]) || file.name

// Blob URLs são revogadas ao trocar de registro e ao desmontar — sem isso cada
// abertura do modal vazaria a memória do arquivo (DF-1 do EvidenceModal).
function revokeAll() {
  for (const file of files.value) {
    if (file.url) URL.revokeObjectURL(file.url)
  }
}

function selectFile(index: number) {
  selectedIndex.value = index
  rotation.value = 0
  zoom.value = 1
}

async function downloadOne(file: EvidenceFile, stationId: number) {
  try {
    const blob = await Network.doctorCheckEvidence.downloadCheckEvidenceFile(stationId, file.id)
    file.url = URL.createObjectURL(blob)
    file.type = blob.type
    file.status = 'ready'
  } catch (error) {
    console.error(error)
    if (isEvidenceUnavailableError(error)) {
      file.status = 'unavailable'
    } else {
      file.status = 'error'
      file.error = getApiErrorMessage(error, 'Não foi possível baixar a evidência')
    }
  }
}

function contextFrom(detail: CheckValidationDetailResponse): CheckEvidenceContext {
  const { doctor } = detail
  return {
    checkin: detail.checkin,
    checkout: detail.checkout,
    timezone: detail.station.timezone || '',
    doctorName: doctor.name,
    crm: doctor.crm ? { number: doctor.crm, uf: doctor.crmUf ?? '' } : null
  }
}

async function load() {
  const { checkId } = props
  isLoading.value = true
  loadError.value = null
  revokeAll()
  files.value = []
  selectedIndex.value = 0

  try {
    const detail = await Network.checkValidation.getDetail(checkId)
    if (checkId !== props.checkId) return

    emit('loaded', contextFrom(detail))

    files.value = detail.evidences.map((evidence) => ({
      id: evidence.id,
      name: evidence.name ?? `evidencia-${evidence.id}`,
      eventType: evidence.eventType,
      url: null,
      type: '',
      status: 'loading' as EvidenceFileStatus,
      error: null
    }))

    await Promise.all(files.value.map((file) => downloadOne(file, detail.station.id)))
    if (checkId !== props.checkId) return

    const firstReady = files.value.findIndex((file) => file.status === 'ready')
    selectedIndex.value = firstReady >= 0 ? firstReady : 0

    if (allUnavailable.value) emit('unavailable')
    else emit('available')
  } catch (error) {
    console.error(error)
    if (checkId === props.checkId) {
      loadError.value = getApiErrorMessage(
        error,
        'Não foi possível carregar as evidências do registro'
      )
    }
  } finally {
    if (checkId === props.checkId) isLoading.value = false
  }
}

watch(
  () => props.checkId,
  () => void load(),
  { immediate: true }
)

onBeforeUnmount(revokeAll)

__expose({ load })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "flex flex-col gap-3 text-left",
    "data-testid": `${_ctx.testidPrefix}-evidence-panel`
  }, [
    (isLoading.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "w-full min-h-[500px] rounded-lg bg-lightGray30 animate-pulse",
          "data-testid": `${_ctx.testidPrefix}-evidence-skeleton`
        }, null, 8, _hoisted_2))
      : (loadError.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 1,
            class: "w-full min-h-[300px] flex flex-col items-center justify-center gap-3 rounded-lg border border-gray-200 p-6 text-center",
            "data-testid": `${_ctx.testidPrefix}-evidence-error`
          }, [
            (_openBlock(), _createElementBlock("svg", _hoisted_4, [
              _createElementVNode("path", {
                d: _unref(mdiAlertCircleOutline),
                fill: "currentColor"
              }, null, 8, _hoisted_5)
            ])),
            _createElementVNode("p", _hoisted_6, _toDisplayString(loadError.value), 1),
            _createVNode(OutlineButton, {
              text: "Tentar novamente",
              width: "200px",
              "data-testid": `${_ctx.testidPrefix}-evidence-retry-button`,
              onClick: load
            }, null, 8, ["data-testid"])
          ], 8, _hoisted_3))
        : (allUnavailable.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 2,
              class: "w-full min-h-[300px] flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center",
              "data-testid": `${_ctx.testidPrefix}-evidence-unavailable`
            }, [
              (_openBlock(), _createElementBlock("svg", _hoisted_8, [
                _createElementVNode("path", {
                  d: _unref(mdiImageOffOutline),
                  fill: "currentColor"
                }, null, 8, _hoisted_9)
              ])),
              _hoisted_10,
              _createElementVNode("p", _hoisted_11, _toDisplayString(files.value.length === 0
            ? 'Este registro não tem arquivo de evidência anexado.'
            : 'O arquivo da evidência não foi encontrado no armazenamento. Tente novamente ou reprove o registro pedindo a redigitação.'), 1),
              (files.value.length > 0)
                ? (_openBlock(), _createBlock(OutlineButton, {
                    key: 0,
                    text: "Tentar novamente",
                    width: "200px",
                    "data-testid": `${_ctx.testidPrefix}-evidence-retry-button`,
                    onClick: load
                  }, null, 8, ["data-testid"]))
                : _createCommentVNode("", true)
            ], 8, _hoisted_7))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
              (files.value.length > 1)
                ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(files.value, (file, index) => {
                      return (_openBlock(), _createElementBlock("button", {
                        key: file.id,
                        type: "button",
                        role: "tab",
                        class: _normalizeClass(["flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm transition-colors", 
            index === selectedIndex.value
              ? 'bg-primary30 border-primary30 text-white font-semibold'
              : 'border-gray-300 text-textPrimary hover:bg-gray-100'
          ]),
                        "aria-selected": index === selectedIndex.value,
                        "data-testid": `${_ctx.testidPrefix}-evidence-file-${file.id}-button`,
                        onClick: ($event: any) => (selectFile(index))
                      }, [
                        (_openBlock(), _createElementBlock("svg", _hoisted_14, [
                          _createElementVNode("path", {
                            d: file.status === 'unavailable' ? _unref(mdiImageOffOutline) : _unref(mdiFileOutline),
                            fill: "currentColor"
                          }, null, 8, _hoisted_15)
                        ])),
                        _createElementVNode("span", _hoisted_16, _toDisplayString(fileLabel(file)), 1)
                      ], 10, _hoisted_13))
                    }), 128))
                  ]))
                : _createCommentVNode("", true),
              (currentFile.value)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    (currentFile.value.status === 'ready' && currentFile.value.url)
                      ? (_openBlock(), _createBlock(StepPreview, {
                          key: 0,
                          file: null,
                          "file-url": currentFile.value.url,
                          "file-type": currentFile.value.type,
                          rotation: rotation.value,
                          zoom: zoom.value,
                          mode: "preview",
                          "show-display-name": false,
                          "onUpdate:rotation": _cache[0] || (_cache[0] = ($event: any) => (rotation.value = $event)),
                          "onUpdate:zoom": _cache[1] || (_cache[1] = ($event: any) => (zoom.value = $event))
                        }, null, 8, ["file-url", "file-type", "rotation", "zoom"]))
                      : (_openBlock(), _createElementBlock("div", {
                          key: 1,
                          class: "w-full min-h-[300px] flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center",
                          "data-testid": `${_ctx.testidPrefix}-evidence-file-unavailable`
                        }, [
                          (_openBlock(), _createElementBlock("svg", _hoisted_18, [
                            _createElementVNode("path", {
                              d: _unref(mdiImageOffOutline),
                              fill: "currentColor"
                            }, null, 8, _hoisted_19)
                          ])),
                          _createElementVNode("p", _hoisted_20, _toDisplayString(currentFile.value.status === 'unavailable'
                ? 'Arquivo indisponível'
                : 'Falha ao baixar o arquivo'), 1),
                          (currentFile.value.error)
                            ? (_openBlock(), _createElementBlock("p", _hoisted_21, _toDisplayString(currentFile.value.error), 1))
                            : _createCommentVNode("", true),
                          _createVNode(OutlineButton, {
                            text: "Tentar novamente",
                            width: "200px",
                            "data-testid": `${_ctx.testidPrefix}-evidence-retry-button`,
                            onClick: load
                          }, null, 8, ["data-testid"])
                        ], 8, _hoisted_17)),
                    _createElementVNode("p", {
                      class: "text-xs text-neutral50 truncate",
                      title: currentFile.value.name
                    }, _toDisplayString(fileLabel(currentFile.value)) + " · " + _toDisplayString(currentFile.value.name), 9, _hoisted_22)
                  ], 64))
                : _createCommentVNode("", true)
            ], 64))
  ], 8, _hoisted_1))
}
}

})